import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import toast from "react-hot-toast";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
	Grid,
	Typography,
	Checkbox,
	Box,
	Divider,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
} from '@mui/material';

import { Visibility, VisibilityOff, InfoOutlined } from '@mui/icons-material';

import { makeStyles } from "tss-react/mui";
import { green } from "@mui/material/colors";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import useQueues from "../../hooks/useQueues";

const useStyles = makeStyles()((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},
	btnWrapper: {
		position: "relative",
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	accessPanel: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
		padding: theme.spacing(1),
		height: 260,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	panelTopRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(0.5),
		gap: theme.spacing(1),
	},
	selectLinks: {
		display: "flex",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(0.5),
		flexShrink: 0,
	},
	selectLink: {
		background: "none",
		border: "none",
		padding: 0,
		cursor: "pointer",
		color: theme.palette.primary.main,
		fontSize: "0.72rem",
		textDecoration: "underline",
		"&:hover": {
			opacity: 0.8,
		},
	},
	scrollList: {
		flex: 1,
		overflowY: "auto",
	},
	colorDot: {
		width: 10,
		height: 10,
		borderRadius: "50%",
		marginRight: theme.spacing(0.75),
		flexShrink: 0,
		display: "inline-block",
	},
	infoBanner: {
		display: "flex",
		alignItems: "flex-start",
		gap: theme.spacing(1),
		backgroundColor: theme.palette.mode === "dark"
			? "rgba(66, 144, 132, 0.15)"
			: "rgba(66, 144, 132, 0.08)",
		border: `1px solid rgba(66, 144, 132, 0.35)`,
		borderRadius: theme.shape.borderRadius,
		padding: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
	},
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
	phone: Yup.string()
		.matches(/^\+?[0-9\s\-().]{7,20}$/, "Invalid phone number")
		.required("Required"),
});

const UserModal = ({ open, onClose, userId }) => {
	const { classes } = useStyles();

	const initialState = {
		name: "",
		email: "",
		phone: "",
		password: "",
		profile: "member"
	};

	const { user: loggedInUser } = useContext(AuthContext);
	const { loading: whatsLoading, whatsApps } = useWhatsApps();
	const { findAll: findAllQueues } = useQueues();

	const [user, setUser] = useState(initialState);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState('');

	// Phone access state
	const [allPhonesAccess, setAllPhonesAccess] = useState(true);
	const [whatsappAccessIds, setWhatsappAccessIds] = useState([]);
	const [phoneSearch, setPhoneSearch] = useState('');

	// Label access state
	const [allLabelsAccess, setAllLabelsAccess] = useState(true);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [labelSearch, setLabelSearch] = useState('');

	const [queues, setQueues] = useState([]);

	useEffect(() => {
		findAllQueues().then(setQueues).catch(toastError);
	}, []);

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => ({ ...prevState, ...data }));
				setWhatsappId(data.whatsappId ?? '');

				const queueIds = data.queues?.map(q => q.id) ?? [];
				setSelectedQueueIds(queueIds);
				setAllLabelsAccess(queueIds.length === 0);

				const wIds = data.whatsapps?.map(w => w.id) ?? [];
				setWhatsappAccessIds(wIds);
				setAllPhonesAccess(wIds.length === 0);
			} catch (err) {
				toastError(err);
			}
		};
		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setAllPhonesAccess(true);
		setWhatsappAccessIds([]);
		setAllLabelsAccess(true);
		setSelectedQueueIds([]);
		setPhoneSearch('');
		setLabelSearch('');
		setWhatsappId('');
	};

	const handleSaveUser = async values => {
		const userData = {
			...values,
			whatsappId,
			queueIds: allLabelsAccess ? [] : selectedQueueIds,
			whatsappAccessIds: allPhonesAccess ? [] : whatsappAccessIds,
		};
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	// Phone panel helpers
	const filteredPhones = whatsApps.filter(w =>
		(w.name ?? "").toLowerCase().includes(phoneSearch.toLowerCase())
	);

	const togglePhone = (id) => {
		if (allPhonesAccess) {
			setAllPhonesAccess(false);
			setWhatsappAccessIds([id]);
		} else {
			const next = whatsappAccessIds.includes(id)
				? whatsappAccessIds.filter(i => i !== id)
				: [...whatsappAccessIds, id];
			setWhatsappAccessIds(next);
		}
	};

	const toggleAllPhones = () => {
		if (allPhonesAccess) {
			setAllPhonesAccess(false);
			setWhatsappAccessIds([...whatsApps.map(w => w.id)]);
		} else {
			setAllPhonesAccess(true);
			setWhatsappAccessIds([]);
		}
	};

	// Label panel helpers
	const filteredQueues = queues.filter(q =>
		q.name.toLowerCase().includes(labelSearch.toLowerCase())
	);

	const toggleLabel = (id) => {
		if (allLabelsAccess) {
			setAllLabelsAccess(false);
			setSelectedQueueIds([id]);
		} else {
			const next = selectedQueueIds.includes(id)
				? selectedQueueIds.filter(i => i !== id)
				: [...selectedQueueIds, id];
			setSelectedQueueIds(next);
		}
	};

	const toggleAllLabels = () => {
		if (allLabelsAccess) {
			setAllLabelsAccess(false);
			setSelectedQueueIds([...queues.map(q => q.id)]);
		} else {
			setAllLabelsAccess(true);
			setSelectedQueueIds([]);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<Field
										as={TextField}
										name="password"
										variant="outlined"
										margin="dense"
										label={i18n.t("userModal.form.password")}
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										type={showPassword ? 'text' : 'password'}
										slotProps={{
											input: {
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={() => setShowPassword((e) => !e)}
														>
															{showPassword ? <VisibilityOff /> : <Visibility />}
														</IconButton>
													</InputAdornment>
												),
											},
										}}
										fullWidth
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label="Phone"
										name="phone"
										error={touched.phone && Boolean(errors.phone)}
										helperText={touched.phone && errors.phone}
										variant="outlined"
										margin="dense"
										fullWidth
										placeholder="+1 555 000 0000"
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("userModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControl
										variant="outlined"
										className={classes.formControl}
										margin="dense"
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>
													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
													>
														<MenuItem value="admin">Admin</MenuItem>
														<MenuItem value="member">Member</MenuItem>
													</Field>
												</>
											)}
										/>
									</FormControl>
								</div>

								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<>
											{/* Default Connection for outbound routing */}
											{!whatsLoading && (
												<FormControl variant="outlined" margin="dense" fullWidth>
													<InputLabel>{i18n.t("userModal.form.whatsapp")}</InputLabel>
													<Select
														value={whatsappId}
														onChange={(e) => setWhatsappId(e.target.value)}
														label={i18n.t("userModal.form.whatsapp")}
													>
														<MenuItem value="">&nbsp;</MenuItem>
														{whatsApps.map((w) => (
															<MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
														))}
													</Select>
												</FormControl>
											)}

											{/* Info banner */}
											<Box className={classes.infoBanner}>
												<InfoOutlined fontSize="small" color="primary" sx={{ mt: "1px", flexShrink: 0 }} />
												<Typography variant="caption" color="text.secondary">
													Members only see pending tickets matching <strong>both</strong> a granted phone connection <strong>and</strong> a granted label. Directly assigned tickets are always visible regardless.
												</Typography>
											</Box>

											{/* Two-panel access control */}
											<Grid container spacing={2}>
												{/* Phone Access panel */}
												<Grid size={6}>
													<Typography variant="subtitle2" gutterBottom fontWeight={600}>
														Phone Access
													</Typography>
													<Box className={classes.accessPanel}>
														<div className={classes.panelTopRow}>
															<TextField
																size="small"
																placeholder="Search phones…"
																value={phoneSearch}
																onChange={e => setPhoneSearch(e.target.value)}
																fullWidth
																margin="none"
															/>
														</div>
														<div className={classes.selectLinks}>
															<button
																type="button"
																className={classes.selectLink}
																onClick={() => { setAllPhonesAccess(true); setWhatsappAccessIds([]); }}
															>
																Select All
															</button>
															<button
																type="button"
																className={classes.selectLink}
																onClick={() => { setAllPhonesAccess(false); setWhatsappAccessIds([]); }}
															>
																Deselect All
															</button>
														</div>
														<List dense className={classes.scrollList} disablePadding>
															<ListItemButton dense onClick={toggleAllPhones} sx={{ py: 0.25 }}>
																<ListItemIcon sx={{ minWidth: 36 }}>
																	<Checkbox
																		size="small"
																		checked={allPhonesAccess}
																		indeterminate={
																			!allPhonesAccess &&
																			whatsappAccessIds.length > 0 &&
																			whatsappAccessIds.length < whatsApps.length
																		}
																		edge="start"
																		disableRipple
																	/>
																</ListItemIcon>
																<ListItemText
																	primary={
																		<Typography variant="body2" fontWeight={600}>
																			All Phones
																		</Typography>
																	}
																/>
															</ListItemButton>
															<Divider />
															{filteredPhones.map(phone => (
																<ListItemButton key={phone.id} dense onClick={() => togglePhone(phone.id)} sx={{ py: 0.25 }}>
																	<ListItemIcon sx={{ minWidth: 36 }}>
																		<Checkbox
																			size="small"
																			checked={allPhonesAccess || whatsappAccessIds.includes(phone.id)}
																			edge="start"
																			disableRipple
																		/>
																	</ListItemIcon>
																	<ListItemText
																		primary={<Typography variant="body2">{phone.name}</Typography>}
																	/>
																</ListItemButton>
															))}
														</List>
													</Box>
												</Grid>

												{/* Queues Access panel */}
												<Grid size={6}>
													<Typography variant="subtitle2" gutterBottom fontWeight={600}>
														Queues Access
													</Typography>
													<Box className={classes.accessPanel}>
														<div className={classes.panelTopRow}>
															<TextField
																size="small"
																placeholder="Search labels…"
																value={labelSearch}
																onChange={e => setLabelSearch(e.target.value)}
																fullWidth
																margin="none"
															/>
														</div>
														<div className={classes.selectLinks}>
															<button
																type="button"
																className={classes.selectLink}
																onClick={() => { setAllLabelsAccess(true); setSelectedQueueIds([]); }}
															>
																Select All
															</button>
															<button
																type="button"
																className={classes.selectLink}
																onClick={() => { setAllLabelsAccess(false); setSelectedQueueIds([]); }}
															>
																Deselect All
															</button>
														</div>
														<List dense className={classes.scrollList} disablePadding>
															<ListItemButton dense onClick={toggleAllLabels} sx={{ py: 0.25 }}>
																<ListItemIcon sx={{ minWidth: 36 }}>
																	<Checkbox
																		size="small"
																		checked={allLabelsAccess}
																		indeterminate={
																			!allLabelsAccess &&
																			selectedQueueIds.length > 0 &&
																			selectedQueueIds.length < queues.length
																		}
																		edge="start"
																		disableRipple
																	/>
																</ListItemIcon>
																<ListItemText
																	primary={
																		<Typography variant="body2" fontWeight={600}>
																			All Queues
																		</Typography>
																	}
																/>
															</ListItemButton>
															<Divider />
															{filteredQueues.map(queue => (
																<ListItemButton key={queue.id} dense onClick={() => toggleLabel(queue.id)} sx={{ py: 0.25 }}>
																	<ListItemIcon sx={{ minWidth: 36 }}>
																		<Checkbox
																			size="small"
																			checked={allLabelsAccess || selectedQueueIds.includes(queue.id)}
																			edge="start"
																			disableRipple
																		/>
																	</ListItemIcon>
																	<ListItemText
																		primary={
																			<Box sx={{ display: "flex", alignItems: "center" }}>
																				<span
																					className={classes.colorDot}
																					style={{ backgroundColor: queue.color }}
																				/>
																				<Typography variant="body2">{queue.name}</Typography>
																			</Box>
																		}
																	/>
																</ListItemButton>
															))}
														</List>
													</Box>
												</Grid>
											</Grid>
										</>
									)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("userModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{userId
										? `${i18n.t("userModal.buttons.okEdit")}`
										: `${i18n.t("userModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default UserModal;
